import React, { useEffect, useState } from 'react';
import { Car, LifeBuoy, Merge, Search } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { toast } from '../hooks/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { WarrantyBadge } from '../pages/postsale/NewCaseDialog';

const ORIG = { compra: ['Comprado na loja', 'text-emerald-400 bg-emerald-400/10'], trade_in: ['Trade-in', 'text-amber-400 bg-amber-400/10'], pos_venda: ['Pós-Venda', 'text-orange-400 bg-orange-400/10'], manual: ['Manual', 'text-muted-foreground bg-muted/40'] };
const fmtD = (s) => (s ? s.slice(0, 10).split('-').reverse().join('/') : '—');

export const CustomerGarage = ({ vehicles = [], cases = [] }) => (
  <div className="space-y-2" data-testid="customer-garage">
    {vehicles.map((v) => {
      const [label, cls] = ORIG[v.origem] || ORIG.manual;
      const vc = cases.filter((c) => c.vin && c.vin === v.vin);
      return (
        <div key={v.id} data-testid={`customer-vehicle-${v.id}`} className="rounded-lg border border-border/70 bg-popover p-3">
          <div className="flex items-center gap-2 flex-wrap">
            <Car className="w-3.5 h-3.5 text-primary" /><p className="font-semibold text-sm">{v.desc}</p>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${cls}`}>{label}</span>
            <span className="text-[11px] text-muted-foreground ml-auto">{fmtD(v.data)}</span>
          </div>
          <p className="text-[11px] text-muted-foreground mt-1 mono">{v.vin || 'sem VIN'}{v.miles ? ` · ${Number(v.miles).toLocaleString()} mi na venda` : ''}{v.deal_id ? ` · ${v.deal_id}` : ''}{vc.length ? ` · ${vc.length} caso(s) de pós-venda` : ''}</p>
        </div>
      );
    })}
    {vehicles.length === 0 && <p className="text-sm text-muted-foreground/70">Nenhum veículo vinculado a este cliente.</p>}
  </div>
);

export const CustomerPostSale = ({ cases = [] }) => (
  <div className="space-y-2" data-testid="customer-postsale">
    {cases.map((c) => (
      <Link key={c.id} to={`/app/post-sale?case=${c.id}`} data-testid={`customer-case-${c.id}`} className="block rounded-lg border border-border/70 bg-popover p-3 hover:border-primary/50 transition-colors">
        <div className="flex items-center gap-2 flex-wrap">
          <LifeBuoy className="w-3.5 h-3.5 text-orange-400" /><p className="font-semibold text-sm">{c.tipo}</p>
          <span className="ent-tag">{c.status}</span><WarrantyBadge w={c.warranty} compact />
          <span className="text-[11px] text-muted-foreground ml-auto">{fmtD(c.created_at)}</span>
        </div>
        <p className="text-[11px] text-muted-foreground mt-1">{c.veiculo || '—'}{c.vin ? ` · …${c.vin.slice(-6)}` : ''}{c.reclamacao ? ` — ${c.reclamacao}` : ''}</p>
      </Link>
    ))}
    {cases.length === 0 && <p className="text-sm text-muted-foreground/70">Nenhum caso de pós-venda.</p>}
  </div>
);

export const MergeCustomerDialog = ({ source, open, onClose, onMerged }) => {
  const [q, setQ] = useState('');
  const [matches, setMatches] = useState([]);
  const [target, setTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setQ(''); setMatches([]); setTarget(null); } }, [open]);
  useEffect(() => {
    if (q.trim().length < 2) { setMatches([]); return; }
    const t = setTimeout(() => api.get('/customers-lookup', { params: { q } }).then((r) => setMatches(r.data.matches.filter((m) => m.id !== source?.id))).catch(() => {}), 300);
    return () => clearTimeout(t);
  }, [q, source]);
  const merge = async () => {
    if (!target) return;
    if (!window.confirm(`Mesclar "${source.nome}" dentro de "${target.nome}"?\n\nTodos os deals, casos, leads, veículos e histórico passam para ${target.nome}. O cadastro "${source.nome}" será removido. Não dá para desfazer.`)) return;
    setBusy(true);
    try { await api.post(`/customers/${source.id}/merge`, { into: target.id }); toast({ title: 'Clientes mesclados', description: `Tudo agora está em ${target.nome}` }); onMerged(target.id); onClose(); }
    catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent aria-describedby={undefined} className="bg-card border-border text-foreground max-w-lg" data-testid="customer-merge-dialog">
        <DialogHeader><DialogTitle className="font-display font-bold text-lg uppercase tracking-tight flex items-center gap-2"><Merge className="w-4 h-4" /> Mesclar cliente duplicado</DialogTitle></DialogHeader>
        <p className="text-[12.5px] text-muted-foreground">Origem: <b className="text-foreground">{source?.nome}</b>. Escolha o cadastro de destino que vai ficar com todo o histórico.</p>
        <div className="relative"><Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground/60" /><input data-testid="customer-merge-search" className="ent-input pl-8" placeholder="Nome, sobrenome, telefone ou VIN do destino" value={q} onChange={(e) => setQ(e.target.value)} autoFocus /></div>
        <div className="space-y-1.5 max-h-[280px] overflow-y-auto">
          {matches.map((m) => (
            <button key={m.id} data-testid={`customer-merge-pick-${m.id}`} onClick={() => setTarget(m)} className={`w-full text-left rounded-md border p-2.5 transition-colors ${target?.id === m.id ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/40'}`}>
              <p className="text-sm font-semibold">{m.nome} <span className="text-[11px] font-normal text-muted-foreground">{m.telefone}</span></p>
              <p className="text-[11px] text-muted-foreground">{m.deals} compra(s) · {m.post_sale_cases} caso(s) · {m.vehicles?.length || 0} veículo(s) · desde {fmtD(m.criadoEm)}</p>
            </button>
          ))}
        </div>
        <DialogFooter><button onClick={onClose} className="ent-btn">Cancelar</button><button data-testid="customer-merge-confirm" onClick={merge} disabled={!target || busy} className="ent-btn ent-btn-primary"><Merge className="w-3.5 h-3.5" /> Mesclar em {target ? target.nome.split(' ')[0] : '…'}</button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
